import {
  ExternalLinkIcon,
  HomeIcon,
  OfficeBuildingIcon,
  PencilIcon,
} from "@heroicons/react/outline";
import { ICellRendererParams } from "ag-grid-community";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ENDPOINTS } from "@/api/application-api";
import * as InlineEdit from "@/components/InlineEdit";
import { CreateOrganizationModal } from "@/components/OrganizationSettings/CreateOrganizationModal";
import { OrganizationDetails } from "@/components/OrganizationSettings/OrganizationDetails";
import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { DataTable } from "@/components/ui/ag-grid-table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TableColumn } from "@/components/ui/TableV2";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { useAuth } from "@/context/auth-context";
import { useActiveOrganization } from "@/hooks/organizations";
import { useDataTableDeleteConfirm } from "@/hooks/useDataTableDeleteConfirm";
import { useWorkspaces } from "@/queries";
import {
  useDeleteOrganization,
  useEditOrganization,
  useOrganizations,
  useSetDefaultOrganization,
} from "@/queries/organizations";
import { useCurrentUser } from "@/queries/users";
import { Organization } from "@/types/organizations";
import { capitalize } from "@/utils/strings";
import { formatTimestamp } from "@/utils/timestamp";

const Organizations = () => {
  const onRequestDeleteConfirm = useDataTableDeleteConfirm();
  const [search, setSearch] = useState("");
  const { data: activeOrganization } = useActiveOrganization();
  const { data: organizationsData, isLoading } = useOrganizations();
  const { mutate: deleteOrganization } = useDeleteOrganization();
  const { data: workspaces } = useWorkspaces();
  const { mutate: setDefaultOrganization } = useSetDefaultOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const userToken = useAuth()?.userToken;
  const { mutateAsync: editOrganization } = useEditOrganization(userToken!);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const filteredOrganizations = organizationsData?.items?.filter(org =>
    org.name.toLowerCase().includes(search.toLowerCase()),
  );
  const { data: currentUser } = useCurrentUser();
  const defaultOrganizationId = currentUser?.default_organization_id;
  const roles = currentUser?.memberships.organizations.map(membership => ({
    organization_id: membership.organization_id,
    role: membership.organization_role,
  }));

  const selectedOrgRole = currentUser?.memberships.organizations.find(
    org => org.organization_id === selectedOrganization?.id,
  )?.organization_role?.name;
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOrganizationClick = (org: Organization) => {
    setSelectedOrganization(org);
    setIsDrawerOpen(true);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("editingOrganization", org.id.toString());
      if (!next.get("tab")) {
        next.set("tab", "members");
      }
      return next;
    });
  };

  const queryClient = useQueryClient();

  const columns: TableColumn<Organization>[] = [
    {
      field: "name",
      name: "name",
      id: 0,
      header: "Name",
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params: ICellRendererParams<Organization>) => {
        const isActive = params.data?.id === activeOrganization?.id;
        return (
          <div className="flex w-full items-center space-x-3">
            <div className="ml-2 flex items-center gap-2">
              <OverflowDisplayTooltip
                content={params.data?.name || ""}
                shouldShow={Boolean(params.data?.name || "")}
              >
                <span className={`text-sm text-gray-900 ${isActive ? "font-semibold" : ""}`}>
                  {params.data?.name || ""} {isActive && "(Current)"}
                </span>
              </OverflowDisplayTooltip>
              {params.data?.id === defaultOrganizationId && (
                <Badge variant={BadgeVariant.GRAY}>Default</Badge>
              )}
            </div>
          </div>
        );
      },
    },
    {
      field: "created_at",
      name: "created_at",
      id: 1,
      header: "Created",
      cellRenderer: (params: ICellRendererParams<Organization>) =>
        params.data?.created_at ? formatTimestamp(params.data.created_at) : "-",
    },
    {
      name: "role",
      id: 2,
      header: "Role",
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params: ICellRendererParams<Organization>) => {
        const userRole = roles?.find(r => r.organization_id === params.data?.id)?.role.name;
        return (
          <Badge variant={userRole === "owner" ? BadgeVariant.BLUE : BadgeVariant.GRAY}>
            {capitalize(userRole || "")}
          </Badge>
        );
      },
    },
    {
      field: "updated_at",
      name: "updated_at",
      id: 3,
      header: "Updated",
      cellRenderer: (params: ICellRendererParams<Organization>) =>
        params.data?.updated_at ? formatTimestamp(params.data.updated_at) : "-",
    },
    {
      field: "members_count",
      name: "members_count",
      id: 4,
      header: "# of Members",
    },
    {
      name: "workspaces_count",
      id: 5,
      header: "# of Workspaces",
      cellRenderer: (params: ICellRendererParams<Organization>) => {
        const orgWorkspaces = workspaces?.workspaces.filter(
          w => w.organization_id === params.data?.id,
        );
        return orgWorkspaces?.length || 0;
      },
    },
  ];

  const actions = useMemo(
    () => [
      {
        id: "open-in-new-tab",
        label: "Open in new tab",
        icon: ExternalLinkIcon,
        getDisabled: (org?: Organization) => {
          const hasWorkspaces = workspaces?.workspaces.some(w => w.organization_id === org?.id);

          return !hasWorkspaces;
        },
        tooltipContent: "No workspaces found",
        onClick: (org?: Organization) => {
          if (!org) {
            return;
          }
          const workspace = workspaces?.workspaces.find(w => w.organization_id === org.id);
          if (!workspace) {
            return;
          }
          window.open(`/workspace/${workspace.id}/home`, "_blank");
        },
      },
      {
        id: "set-default",
        label: "Set as default",
        icon: HomeIcon,
        tooltipContent: "No workspaces found",
        getDisabled: (org?: Organization) => {
          if (!org) {
            return false;
          }

          const hasWorkspaces = workspaces?.workspaces.some(w => w.organization_id === org?.id);

          if (!hasWorkspaces) {
            return true;
          }

          return currentUser?.default_organization_id === org.id;
        },
        onClick: (org?: Organization) => {
          if (!org) {
            return;
          }
          setDefaultOrganization({ organization_id: org.id });
        },
      },
    ],
    [setDefaultOrganization, currentUser, workspaces],
  );

  const contextMenuItems = useMemo(
    () => [
      {
        id: "open-in-new-tab",
        label: "Open in new tab",
        icon: ExternalLinkIcon,
        getDisabled: (org?: Organization) => {
          const hasWorkspaces = workspaces?.workspaces.some(w => w.organization_id === org?.id);
          return !hasWorkspaces;
        },
        onClick: (org?: Organization) => {
          if (!org) {
            return;
          }
          const workspace = workspaces?.workspaces.find(w => w.organization_id === org.id);
          if (!workspace) {
            return;
          }
          window.open(`/workspace/${workspace.id}/home`, "_blank");
        },
        tooltipContent: "No workspaces found",
      },
      {
        id: "set-default",
        label: "Set as default",
        icon: HomeIcon,
        getDisabled: (org?: Organization) => {
          if (!org) {
            return false;
          }

          const hasWorkspaces = workspaces?.workspaces.some(w => w.organization_id === org?.id);

          if (!hasWorkspaces) {
            return true;
          }

          return currentUser?.default_organization_id === org.id;
        },
        onClick: (org?: Organization) => {
          if (!org) {
            return;
          }
          setDefaultOrganization({
            organization_id: org.id,
          });
        },
      },
    ],
    [setDefaultOrganization, currentUser, workspaces],
  );

  useEffect(() => {
    if (isDrawerOpen) {
      return;
    }

    const editingOrganization = searchParams.get("editingOrganization");
    if (editingOrganization) {
      const organization = organizationsData?.items?.find(
        org => org.id.toString() === editingOrganization,
      );
      if (organization) {
        handleOrganizationClick(organization);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationsData?.items, searchParams]);

  const handleRenameOrganization = (name: string) => {
    if (name.trim() && name !== selectedOrganization?.name) {
      editOrganization({
        organizationId: selectedOrganization?.id || 0,
        name: name.trim(),
      });
    }
  };

  const onOpenChange = (open: boolean) => {
    setIsDrawerOpen(open);
    if (!open) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete("editingOrganization");
        next.delete("tab");
        return next;
      });
      setSelectedOrganization(null);
    }
    queryClient.removeQueries([ENDPOINTS.workspaces]);
    queryClient.removeQueries([ENDPOINTS.organization_members]);
  };

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-hidden">
          <DataTable
            data={filteredOrganizations || []}
            columns={columns}
            isLoading={isLoading}
            rowsDeletable
            onDelete={org => {
              if (!org) {
                return;
              }
              deleteOrganization(org.id);
            }}
            onRequestDeleteConfirm={onRequestDeleteConfirm}
            onRowClick={org => handleOrganizationClick(org)}
            action={<CreateOrganizationModal isOpen={isOpen} setIsOpen={setIsOpen} />}
            actions={actions}
            contextMenuItems={contextMenuItems}
            objectName="organization"
            search={search}
            setSearch={setSearch}
            hasSearch
            searchPlaceholder="Search organizations"
            emptyStateMessage="No organizations found"
            isHighlighted={org => org.id === Number(activeOrganization?.id)}
          />
        </div>
      </div>

      <Sheet open={isDrawerOpen} onOpenChange={onOpenChange}>
        <SheetContent className="w-full min-w-[60vw] gap-0">
          <SheetHeader className="pb-0">
            <SheetTitle className="flex h-[36px] items-center gap-1 text-2xl font-semibold">
              <OfficeBuildingIcon className="max-h-5 min-h-5 min-w-5 max-w-5 text-gray-400" />
              <InlineEdit.Root
                initialValue={selectedOrganization?.name || ""}
                onEdit={handleRenameOrganization}
                readOnly={selectedOrgRole !== "owner"}
                className="min-w-0 max-w-[386px] flex-1 p-0"
              >
                <InlineEdit.Trigger className="flex h-9 items-center rounded-md border border-transparent px-3">
                  <span className="font-semibold text-gray-900">{selectedOrganization?.name}</span>
                </InlineEdit.Trigger>
                <InlineEdit.EditTrigger>
                  <PencilIcon className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                </InlineEdit.EditTrigger>
                <InlineEdit.Input className="h-9 py-0 text-2xl font-semibold" />
                <InlineEdit.Rename>Rename</InlineEdit.Rename>
                <InlineEdit.Cancel>Cancel</InlineEdit.Cancel>
              </InlineEdit.Root>
            </SheetTitle>
          </SheetHeader>

          {selectedOrganization && (
            <OrganizationDetails
              organization={selectedOrganization}
              userRole={selectedOrgRole || ""}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Organizations;
